import { useMemo, useState } from 'react';
import type { ThemeKey } from '@/pages/home/themes';
import {
  CONTENT_INK,
  CONTENT_MUTED,
  CONTENT_BORDER,
  CONTENT_GREEN,
  accentOf,
} from '@/pages/home/contentTokens';

type JobId = 'all' | 'ai' | 'web' | 'cloud';
type RegionId = 'all' | 'seoul' | 'gyeongin' | 'etc';

const JOB_FILTERS: { id: JobId; label: string }[] = [
  { id: 'all', label: '전체' },
  { id: 'ai', label: 'AI·데이터' },
  { id: 'web', label: '웹·백엔드' },
  { id: 'cloud', label: '클라우드' },
];

const REGION_FILTERS: { id: RegionId; label: string }[] = [
  { id: 'all', label: '전국' },
  { id: 'seoul', label: '서울' },
  { id: 'gyeongin', label: '경기·인천' },
  { id: 'etc', label: '기타' },
];

type Matrix = Record<Exclude<JobId, 'all'>, Record<Exclude<RegionId, 'all'>, number>>;

const JOBS_MATRIX: Matrix = {
  ai: { seoul: 520, gyeongin: 400, etc: 288 },
  web: { seoul: 600, gyeongin: 510, etc: 376 },
  cloud: { seoul: 444, gyeongin: 432, etc: 442 },
};
const JOBS_BY_JOB = { ai: 1208, web: 1486, cloud: 1318 };
const JOBS_BY_REGION = { seoul: 1564, gyeongin: 1342, etc: 1106 };
const JOBS_TOTAL = 4012;

const COMPANIES_MATRIX: Matrix = {
  ai: { seoul: 322, gyeongin: 290, etc: 200 },
  web: { seoul: 372, gyeongin: 330, etc: 245 },
  cloud: { seoul: 309, gyeongin: 286, etc: 257 },
};
const COMPANIES_BY_JOB = { ai: 812, web: 947, cloud: 852 };
const COMPANIES_BY_REGION = { seoul: 1003, gyeongin: 906, etc: 702 };
const COMPANIES_TOTAL = 2611;

interface MetricSet {
  matrix: Matrix;
  byJob: Record<Exclude<JobId, 'all'>, number>;
  byRegion: Record<Exclude<RegionId, 'all'>, number>;
  total: number;
}

const JOBS_METRIC: MetricSet = { matrix: JOBS_MATRIX, byJob: JOBS_BY_JOB, byRegion: JOBS_BY_REGION, total: JOBS_TOTAL };
const COMPANIES_METRIC: MetricSet = { matrix: COMPANIES_MATRIX, byJob: COMPANIES_BY_JOB, byRegion: COMPANIES_BY_REGION, total: COMPANIES_TOTAL };

function resolveMetric(set: MetricSet, job: JobId, region: RegionId): number {
  if (job === 'all' && region === 'all') return set.total;
  if (job === 'all') return set.byRegion[region as Exclude<RegionId, 'all'>];
  if (region === 'all') return set.byJob[job as Exclude<JobId, 'all'>];
  return set.matrix[job as Exclude<JobId, 'all'>][region as Exclude<RegionId, 'all'>];
}

function ChipRow<T extends string>({
  label, options, value, onChange, accent,
}: {
  label: string;
  options: { id: T; label: string }[];
  value: T;
  onChange: (next: T) => void;
  accent: string;
}) {
  return (
    <div className="flex items-center gap-3">
      <span className="w-8 shrink-0 text-[10px] tracking-[0.2em] uppercase" style={{ color: CONTENT_MUTED }}>
        {label}
      </span>
      <div className="flex flex-wrap gap-1.5">
        {options.map((option) => {
          const active = value === option.id;
          return (
            <button
              key={option.id}
              type="button"
              aria-pressed={active}
              onClick={() => onChange(option.id)}
              className="whitespace-nowrap cursor-pointer rounded-full px-3 h-[28px] text-[11px] font-semibold transition-all"
              style={{
                background: active ? accent : '#fff',
                color: active ? '#fff' : CONTENT_INK,
                border: `1px solid ${active ? accent : CONTENT_BORDER}`,
              }}
            >
              {option.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function MiniDonut({ value, color, label, sub }: { value: number; color: string; label: string; sub: string }) {
  return (
    <div className="flex flex-col items-center gap-2.5">
      <div
        className="relative size-[96px] rounded-full"
        style={{ background: `conic-gradient(${color} ${value}%, rgba(20,18,16,0.10) 0)` }}
        role="img"
        aria-label={`${label} ${value}퍼센트`}
      >
        <div className="absolute inset-[9px] flex items-center justify-center rounded-full" style={{ background: '#fff' }}>
          <span className="font-logo-b text-[24px] leading-none" style={{ color: CONTENT_INK, letterSpacing: '-0.03em' }}>
            {value}%
          </span>
        </div>
      </div>
      <div className="text-center">
        <div className="text-[12px] font-semibold" style={{ color: CONTENT_INK }}>{label}</div>
        <div className="text-[10px]" style={{ color: CONTENT_MUTED }}>{sub}</div>
      </div>
    </div>
  );
}

export default function TrustPanelSection({ theme }: { theme: ThemeKey }) {
  const accent = accentOf(theme);
  const [job, setJob] = useState<JobId>('all');
  const [region, setRegion] = useState<RegionId>('all');

  const jobCount = useMemo(() => resolveMetric(JOBS_METRIC, job, region), [job, region]);
  const companyCount = useMemo(() => resolveMetric(COMPANIES_METRIC, job, region), [job, region]);

  const filterKey = `${job}-${region}`;
  const filterLabel = [
    JOB_FILTERS.find((f) => f.id === job)?.label,
    REGION_FILTERS.find((f) => f.id === region)?.label,
  ]
    .filter((label, idx) => (idx === 0 ? label !== '전체' : label !== '전국'))
    .join(' · ');

  return (
    <section id="trust" className="max-w-[1180px] mx-auto px-8 md:px-14 pt-16 pb-24">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-12">
        <div className="lg:col-span-7">
          <div className="text-[11px] font-semibold tracking-wide mb-6" style={{ color: accent }}>
            취업 현실 팩트
          </div>
          <h2
            className="font-logo-b text-3xl md:text-4xl leading-tight"
            style={{ color: CONTENT_INK, letterSpacing: '-0.04em' }}
          >
            학원 말고,<br />공공데이터로.
          </h2>
        </div>
        <div className="lg:col-span-5 flex items-end">
          <p style={{ fontSize: '15px', lineHeight: 1.7, color: CONTENT_INK, opacity: 0.7 }}>
            직무와 지역을 좁혀 보세요. 실제 채용공고 수와 고용보험 고용순증 기업 수가
            공공데이터 기준으로 즉시 다시 계산됩니다.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-stretch">
        {/* 좌측 — 지표 + 필터 */}
        <div className="lg:col-span-7">
          <div
            className="h-full flex flex-col rounded-[36px] p-6 md:p-8"
            style={{ border: `1px solid ${CONTENT_BORDER}`, background: `${accent}0a` }}
          >
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase" style={{ color: CONTENT_MUTED }}>
                <i className="ri-database-2-line text-[14px]" style={{ color: accent }} />
                실시간 공공데이터 신뢰 지표
              </span>
              <span className="inline-flex items-center gap-1.5 text-[10px] font-bold px-2.5 h-[22px] rounded-full" style={{ color: CONTENT_GREEN, border: '1px solid rgba(62,142,90,0.3)', background: 'rgba(62,142,90,0.08)' }}>
                <span className="relative flex size-1.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75" style={{ background: CONTENT_GREEN }} />
                  <span className="relative inline-flex size-1.5 rounded-full" style={{ background: CONTENT_GREEN }} />
                </span>
                LIVE
              </span>
            </div>

            <div className="mt-5 space-y-3">
              <ChipRow label="직무" options={JOB_FILTERS} value={job} onChange={setJob} accent={accent} />
              <ChipRow label="지역" options={REGION_FILTERS} value={region} onChange={setRegion} accent={accent} />
            </div>

            <div className="mt-7">
              <div key={`j-${filterKey}`} className="content-pop flex items-baseline gap-1.5">
                <span className="font-logo-b text-4xl md:text-5xl leading-none" style={{ color: CONTENT_INK, letterSpacing: '-0.04em' }}>
                  {jobCount.toLocaleString('ko-KR')}
                </span>
                <span className="text-[14px] font-semibold" style={{ color: CONTENT_MUTED }}>건</span>
              </div>
              <p className="mt-2 text-[12px]" style={{ color: CONTENT_MUTED }}>
                워크넷 실채용 공고{filterLabel ? ` · ${filterLabel}` : ' · 전 직무 · 전국'}
              </p>
            </div>

            <div className="mt-5 flex items-center gap-3 rounded-[24px] p-4" style={{ background: '#fff', border: `1px solid ${CONTENT_BORDER}` }}>
              <span className="flex w-10 h-10 shrink-0 items-center justify-center rounded-2xl" style={{ background: `${accent}14`, color: accent }}>
                <i className="ri-building-4-line text-[18px]" />
              </span>
              <div className="min-w-0">
                <div className="text-[11px]" style={{ color: CONTENT_MUTED }}>IT 고용보험 고용순증 추적</div>
                <div key={`c-${filterKey}`} className="content-pop flex items-baseline gap-1">
                  <span className="font-logo-b text-[28px] leading-none" style={{ color: accent, letterSpacing: '-0.03em' }}>
                    {companyCount.toLocaleString('ko-KR')}
                  </span>
                  <span className="text-[11px] font-semibold" style={{ color: CONTENT_MUTED }}>개사</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 우측 — 수료율 vs 취업률 */}
        <div className="lg:col-span-5">
          <div
            className="h-full flex flex-col rounded-[36px] p-6 md:p-8"
            style={{ border: `1px solid ${CONTENT_BORDER}`, background: '#fff' }}
          >
            <div className="flex items-center justify-between">
              <span className="text-[10px] tracking-[0.25em] uppercase" style={{ color: CONTENT_MUTED }}>
                수료율 vs 취업률
              </span>
              <span className="text-[10px]" style={{ color: CONTENT_MUTED }}>2026 실측</span>
            </div>

            <div className="mt-7 flex items-center justify-around gap-2">
              <MiniDonut value={89} color={CONTENT_GREEN} label="수료율" sub="끝까지 듣는 비율" />
              <MiniDonut value={71} color={accent} label="취업률" sub="수료 후 취업 비율" />
            </div>

            <p className="mt-6 text-[12px] leading-relaxed" style={{ color: CONTENT_MUTED }}>
              수료는 쉽지만 취업은 다릅니다. 이 <strong style={{ color: CONTENT_INK }}>18%p</strong>의 간극이
              학원이 말해주지 않는 숫자입니다.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}